// Location: src/pages/plan-summary/components/BestWorstDays.tsx

import { useTranslation } from 'react-i18next';

interface Day {
  date: string;
  day_number: number;
  reason: string;
  calories?: number;
}

interface BestWorstDaysProps {
  bestDays: Day[];
  worstDays: Day[];
}

export default function BestWorstDays({ bestDays, worstDays }: BestWorstDaysProps) {
  const { t, i18n } = useTranslation();

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(i18n.language, { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getInsightMessage = () => {
    if (bestDays && bestDays.length > worstDays.length) {
      return t('planSummary.performanceHighlights.insights.moreBest');
    } else if (worstDays && worstDays.length > 0) {
      return t('planSummary.performanceHighlights.insights.moreWorst');
    } else {
      return t('planSummary.performanceHighlights.insights.consistent');
    }
  };

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-3xl p-6 lg:p-8 backdrop-blur-sm flex flex-col h-full">
      <div className="mb-8">
        <h3 className="text-xl font-light text-white tracking-tight">{t('planSummary.performanceHighlights.title')}</h3>
        <p className="text-xs text-gray-500 mt-1">{t('planSummary.performanceHighlights.subtitle')}</p>
      </div>

      <div className="flex-grow space-y-8">
        {/* Best Days */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.8)]"></div>
            <h4 className="text-[10px] text-gray-400 uppercase tracking-widest font-medium">{t('planSummary.performanceHighlights.bestDays.title')}</h4>
          </div>

          {bestDays && bestDays.length > 0 ? (
            <div className="space-y-3">
              {bestDays.map((day, index) => (
                <div
                  key={index}
                  className="group relative pl-4 py-3 border-l-2 border-green-500/50 hover:border-green-400 bg-gradient-to-r from-green-500/[0.02] to-transparent transition-colors"
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-green-400 text-sm font-medium">
                        {t('planSummary.performanceHighlights.bestDays.day', { number: day.day_number })}
                      </span>
                      <span className="text-gray-500 text-xs">
                        {formatDate(day.date)}
                      </span>
                    </div>
                  </div>
                  <div className="text-gray-300 text-sm font-light leading-relaxed">{day.reason}</div>
                  {day.calories && (
                    <div className="text-gray-500 text-xs mt-1.5 flex items-center gap-1">
                      <i className="ri-fire-line text-[10px]"></i> {day.calories} kcal
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="pl-4 py-3 border-l-2 border-white/5">
              <div className="text-sm text-gray-500 font-light italic">{t('planSummary.performanceHighlights.bestDays.empty')}</div>
            </div>
          )}
        </div>

        {/* Worst Days */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1.5 h-1.5 rounded-full bg-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.8)]"></div>
            <h4 className="text-[10px] text-gray-400 uppercase tracking-widest font-medium">{t('planSummary.performanceHighlights.worstDays.title')}</h4>
          </div>

          {worstDays && worstDays.length > 0 ? (
            <div className="space-y-3">
              {worstDays.map((day, index) => (
                <div
                  key={index}
                  className="group relative pl-4 py-3 border-l-2 border-orange-500/50 hover:border-orange-400 bg-gradient-to-r from-orange-500/[0.02] to-transparent transition-colors"
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-orange-400 text-sm font-medium">
                        {t('planSummary.performanceHighlights.worstDays.day', { number: day.day_number })}
                      </span>
                      <span className="text-gray-500 text-xs">
                        {formatDate(day.date)}
                      </span>
                    </div>
                  </div>
                  <div className="text-gray-300 text-sm font-light leading-relaxed">{day.reason}</div>
                  {day.calories && (
                    <div className="text-gray-500 text-xs mt-1.5 flex items-center gap-1">
                      <i className="ri-fire-line text-[10px]"></i> {day.calories} kcal
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="pl-4 py-3 border-l-2 border-white/5">
              <div className="text-sm text-gray-500 font-light italic">{t('planSummary.performanceHighlights.worstDays.empty')}</div>
            </div>
          )}
        </div>
      </div>

      {/* Insight box - refined and elegant */}
      <div className="mt-8 pt-6 border-t border-white/5">
        <div className="bg-blue-500/[0.03] border border-blue-500/10 rounded-2xl p-4 flex items-start gap-4">
          <div className="w-8 h-8 rounded-full bg-blue-500/10 text-blue-400 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-lightbulb-flash-line"></i>
          </div>
          <div>
            <div className="text-[10px] text-blue-400/80 uppercase tracking-widest font-medium mb-1">{t('common.keyTakeaway')}</div>
            <div className="text-gray-300 text-sm font-light leading-relaxed">
              {getInsightMessage()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}